import React, {
  useState,
  KeyboardEvent,
  ChangeEvent,
  useRef,
  useEffect,
} from "react";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

interface LeadOption {
  id: string;
  name: string;
}

interface ProfessionalLeadsSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: LeadOption[];
  className?: string;
}

const ProfessionalLeadsSelector: React.FC<ProfessionalLeadsSelectorProps> = ({
  value = [],
  onChange,
  options,
  className = "",
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<LeadOption[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showDropdown) return;

    const selected = new Set(value);
    const availableOptions = options.filter((option) => !selected.has(option.id));

    if (inputValue.trim() === "") {
      setFilteredOptions(availableOptions);
      return;
    }

    const lowerInput = inputValue.toLowerCase();
    setFilteredOptions(
      availableOptions.filter((option) =>
        option.name.toLowerCase().includes(lowerInput)
      )
    );
  }, [inputValue, value, showDropdown, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getLeadById = (id: string) => options.find((option) => option.id === id);

  const addLead = (leadId: string) => {
    if (leadId && !value.includes(leadId)) {
      onChange([...value, leadId]);
    }
    setInputValue("");
    setShowDropdown(false);
  };

  const removeLead = (leadId: string) => {
    onChange(value.filter((id) => id !== leadId));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        addLead(filteredOptions[0].id);
      }
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      onChange(value.slice(0, -1));
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (!showDropdown) {
      setShowDropdown(true);
    }
  };

  return (
    <div className="relative w-full">
      <div
        className={`w-full border rounded-lg p-2 flex items-center flex-wrap gap-2 bg-white focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${className}`}
      >
        {value.map((leadId) => {
          const lead = getLeadById(leadId);
          if (!lead) return null;

          return (
            <span
              key={leadId}
              className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-[13px] flex items-center gap-x-1 group"
            >
              {lead.name}
              <button
                type="button"
                onClick={() => removeLead(leadId)}
                className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
              >
                <X size={13} />
              </button>
            </span>
          );
        })}
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowDropdown(true)}
          className="flex-1 !border-none shadow-none !ring-0 !outline-none min-w-[120px] h-8 p-0"
          placeholder={value.length === 0 ? "Click here to select team leads" : ""}
        />
      </div>

      {showDropdown && filteredOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredOptions.map((option) => (
            <div
              key={option.id}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100"
              onClick={() => addLead(option.id)}
            >
              {option.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfessionalLeadsSelector;
